// animatedLoops/AnimatedLoopLibrary.jsx
//
// The Animated Loop / GIF picker panel: a scrollable grid of stock loops + the
// "Loop colors" recolor strip. Selecting a loop, or recolouring it, applies the
// value immediately via onSelect(dataUri, loop, meta) — that string is a
// self-contained animated SVG data URI that flows through the existing wallpaper /
// portrait / media `src` with no render-site changes.
//
// Reduced-motion: the picker tiles never all animate (frozen-poster-on-tiles),
// and the APPLIED value is a static poster when the creator's OS prefers reduced
// motion, so a reduced-motion creator applies a still image, not a moving one.
//
// A11y (NEM-5): the grid is a TRUE listbox — roving tabindex + Arrow/Home/End
// keyboard navigation, aria-activedescendant tracks the focused option — so the
// announced "listbox / option" semantics match real behaviour (not 20 tab stops).
//
// Play-one-at-a-time (NEM-8): at most one tile animates — the one under
// pointer/focus (`activeId`), or the selected one when nothing is active.

import React, { useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import useReducedMotion from "../../hooks/useReducedMotion";
import { LOOPS, DEFAULT_PALETTE_ID, getLoopById, loopDataUri } from "./catalog";
import AnimatedLoopTile from "./AnimatedLoopTile";
import LoopColorStrip from "./LoopColorStrip";

const GRID_COLS = 4; // keep in sync with gridTemplateColumns below

const AnimatedLoopLibrary = ({
  onSelect,
  segmentColor,
  wheelColor,
  initialLoopId = null,
  initialPalette = DEFAULT_PALETTE_ID,
}) => {
  const reduced = useReducedMotion();
  const [selectedLoopId, setSelectedLoopId] = useState(initialLoopId);
  const [palette, setPalette] = useState(initialPalette);
  // Which single tile animates: the pointer/keyboard-focused one, else selected.
  const [activeId, setActiveId] = useState(null);
  // Roving tabindex: index of the tile that is the current tab stop.
  const initialFocus = Math.max(
    0,
    LOOPS.findIndex((l) => l.id === initialLoopId)
  );
  const [focusIndex, setFocusIndex] = useState(initialFocus);
  const tileRefs = useRef([]);

  const apply = (loopId, pal) => {
    const loop = getLoopById(loopId);
    if (!loop || !onSelect) return;
    // Creator-side reduced-motion respect: store a static poster.
    onSelect(loopDataUri(loop, pal, { animated: !reduced }), loop, {
      loopId,
      palette: pal,
    });
  };

  const handleSelectLoop = (loop) => {
    setSelectedLoopId(loop.id);
    const idx = LOOPS.findIndex((l) => l.id === loop.id);
    if (idx >= 0) setFocusIndex(idx);
    apply(loop.id, palette);
  };

  const handlePaletteChange = (pal) => {
    setPalette(pal);
    if (selectedLoopId) apply(selectedLoopId, pal); // re-apply in new colours
  };

  const activate = useCallback((id) => setActiveId(id), []);
  const deactivate = useCallback(
    (id) => setActiveId((prev) => (prev === id ? null : prev)),
    []
  );

  const moveFocus = useCallback((next) => {
    const clamped = Math.max(0, Math.min(LOOPS.length - 1, next));
    setFocusIndex(clamped);
    const el = tileRefs.current[clamped];
    if (el) el.focus();
  }, []);

  const onKeyDown = useCallback(
    (e) => {
      const i = focusIndex;
      switch (e.key) {
        case "ArrowRight":
          moveFocus(i + 1);
          break;
        case "ArrowLeft":
          moveFocus(i - 1);
          break;
        case "ArrowDown":
          moveFocus(i + GRID_COLS);
          break;
        case "ArrowUp":
          moveFocus(i - GRID_COLS);
          break;
        case "Home":
          moveFocus(0);
          break;
        case "End":
          moveFocus(LOOPS.length - 1);
          break;
        default:
          return; // let Enter/Space fall through to the native button
      }
      e.preventDefault();
    },
    [focusIndex, moveFocus]
  );

  const count = useMemo(() => LOOPS.length, []);
  // The single loop id currently animating (NEM-8).
  const animatingId = activeId || selectedLoopId;
  const focusedLoop = LOOPS[focusIndex];

  return (
    <div>
      <div
        style={{
          fontFamily: "DM Sans, sans-serif",
          fontSize: 12,
          color: "#565556",
          margin: "8px 0",
        }}
      >
        {count} seamless loops · scroll for more ⌄
      </div>

      <div
        role="listbox"
        aria-label="Animated loops"
        aria-activedescendant={focusedLoop ? `al-opt-${focusedLoop.id}` : undefined}
        onKeyDown={onKeyDown}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 8,
          maxHeight: 226,
          overflowY: "auto",
          paddingRight: 2,
        }}
      >
        {LOOPS.map((loop, idx) => (
          <AnimatedLoopTile
            key={loop.id}
            ref={(el) => (tileRefs.current[idx] = el)}
            loop={loop}
            palette={palette}
            selected={selectedLoopId === loop.id}
            animate={animatingId === loop.id}
            tabIndex={idx === focusIndex ? 0 : -1}
            onSelect={handleSelectLoop}
            onActivate={activate}
            onDeactivate={deactivate}
          />
        ))}
      </div>

      <LoopColorStrip
        value={palette}
        onChange={handlePaletteChange}
        segmentColor={segmentColor}
        wheelColor={wheelColor}
      />
    </div>
  );
};

AnimatedLoopLibrary.propTypes = {
  onSelect: PropTypes.func.isRequired,
  segmentColor: PropTypes.string,
  wheelColor: PropTypes.string,
  initialLoopId: PropTypes.string,
  initialPalette: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default AnimatedLoopLibrary;
